import axios from "axios"
import { API_HOST } from "../BaseUrl"
import { headers } from "./config"
import { generateHMAC } from "../../Utils/hmac"
import { clearPhonepeSession, getPhonepeToken } from "../../Components/PhonepePortal/phonepeSession"

// Reimbursement API. The member is identified ONLY by their patient token (issued at
// verify-passcode) — never by an email in the request, so nobody can read or change another
// member's reimbursement by editing a parameter. A 401 means the session ended: the phonepe
// session is cleared and the member is sent back to sign in.
const client = axios.create()

client.interceptors.request.use(async (config) => {
    const hmac = await generateHMAC()
    const token = getPhonepeToken()
    // Shared defaults only where the call did not set its own (uploads send multipart).
    Object.entries(headers).forEach(([name, value]) => config.headers.set(name, value, false))
    config.headers.set("X-Rt", hmac.timestamp)
    config.headers.set("X-Hmac", hmac.token)
    if (token) config.headers.set("Authorization", `Bearer ${token}`)
    return config
})

client.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            clearPhonepeSession()
            window.location.assign('/phonepe/login')
        }
        return Promise.reject(error)
    }
)

// Uploads a single reimbursement form file to S3 as soon as it's picked in the browser, returning
// its URL. `type` is 'prescription'|'claim', `field` is the upload slot (e.g. 'prescription_file',
// 'upper_occlusal_photo'). Optionally reports progress (axios `onUploadProgress`) and can be
// cancelled with an AbortController `signal`.
export const uploadReimbursementFile = async (file, { type, field }, { onUploadProgress, signal } = {}) => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('type', type)
    formData.append('field', field)
    return client.post(API_HOST + `insurance/reimbursement/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress,
        signal,
    })
}

// Starts a Reimbursement (it goes straight into Reimbursement Prescription Review).
export const submitPrescription = async (data) =>
    client.post(API_HOST + `insurance/reimbursement/prescription`, data)

// Re-uploads the prescription documents a reviewer asked for ({ <field>: <new url> }).
export const resubmitPrescription = async (id, data) =>
    client.put(API_HOST + `insurance/reimbursement/prescription/${encodeURIComponent(id)}`, data)

// The member's latest Reimbursement, with its current proof of treatment.
export const getPrescriptionStatus = async () =>
    client.get(API_HOST + `insurance/reimbursement/prescription`)

// Every open Reimbursement of the member, newest first, each with its current proof of treatment.
// A member may have several open at once.
export const getOpenReimbursements = async () =>
    client.get(API_HOST + `insurance/reimbursement/prescription/open`)

// The member's own Reimbursements, newest first.
export const getPrescriptionHistory = async (page = 1) =>
    client.get(API_HOST + `insurance/reimbursement/prescription/history?page=${page}`)

// Uploads proof of treatment for an approved Reimbursement.
export const submitClaim = async (data) =>
    client.post(API_HOST + `insurance/reimbursement/claim`, data)

// Re-uploads the treatment documents a reviewer asked for ({ <field>: <new url> }).
export const resubmitClaim = async (id, data) =>
    client.put(API_HOST + `insurance/reimbursement/claim/${encodeURIComponent(id)}`, data)

export const getClaimStatus = async () =>
    client.get(API_HOST + `insurance/reimbursement/claim`)

export const getClaimHistory = async (page = 1) =>
    client.get(API_HOST + `insurance/reimbursement/claim/history?page=${page}`)

// A short-lived link to view one of the member's stored files (the bucket is private). Fetch a
// fresh one each time a file is shown. `claimId` picks one proof of treatment's file; without it
// the API uses the current proof.
export const getReimbursementFileUrl = async (id, field, index = 0, claimId) =>
    client.get(API_HOST + `insurance/reimbursement/${encodeURIComponent(id)}/files?field=${encodeURIComponent(field)}&index=${index}`
        + (claimId ? `&claim_id=${encodeURIComponent(claimId)}` : ''))
